import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import {
  Loader2, Save, ShieldCheck, AlertTriangle, FileText,
} from "lucide-react";
import { inputCls } from "./constants";

// Cards are grouped to mirror the LLM extraction schema in CLARITY_EXTRACT_SYSTEM.
// Each field has: section_key, field_key, label, type=text|textarea, hint?
const SECTIONS = [
  {
    key: "client_basics",
    title: "Owner Snapshot",
    eyebrow: "Who & where",
    fields: [
      { k: "client_name", label: "Client Name" },
      { k: "business_name", label: "Business Name" },
      { k: "client_email", label: "Email" },
      { k: "client_phone", label: "Phone" },
      { k: "website", label: "Website" },
      { k: "industry", label: "Industry" },
      { k: "location", label: "Location" },
      { k: "team_size", label: "Team Size" },
      { k: "revenue_range", label: "Revenue Range" },
      { k: "business_stage", label: "Business Stage" },
    ],
  },
  {
    key: "business_model",
    title: "Business Model",
    eyebrow: "What they sell",
    fields: [
      { k: "what_they_sell", label: "What they sell", type: "textarea" },
      { k: "ideal_customer", label: "Ideal customer", type: "textarea" },
      { k: "revenue_streams", label: "Revenue streams", type: "textarea" },
      { k: "sales_model", label: "Sales model" },
      { k: "delivery_model", label: "Delivery model" },
    ],
  },
  {
    key: "why_now",
    title: "Why Now",
    eyebrow: "Trigger & urgency",
    fields: [
      { k: "trigger_event", label: "Trigger event", type: "textarea" },
      { k: "urgency", label: "Urgency" },
      { k: "cost_of_doing_nothing", label: "Cost of doing nothing", type: "textarea" },
    ],
  },
  {
    key: "owner_goals",
    title: "Owner Goals (30/60/90)",
    eyebrow: "What success looks like",
    fields: [
      { k: "thirty_day_win", label: "30-day win", type: "textarea" },
      { k: "sixty_day_win", label: "60-day win", type: "textarea" },
      { k: "ninety_day_win", label: "90-day win", type: "textarea" },
      { k: "primary_metrics", label: "Primary metrics" },
      { k: "desired_outcome", label: "Desired outcome", type: "textarea" },
    ],
  },
  {
    key: "current_friction",
    title: "Current Friction",
    eyebrow: "Pain points & leaks",
    fields: [
      { k: "summary", label: "Friction summary", type: "textarea" },
      { k: "time_leaks", label: "Time leaks (one per line)", type: "list" },
      { k: "money_leaks", label: "Money leaks (one per line)", type: "list" },
      { k: "opportunity_leaks", label: "Opportunity leaks (one per line)", type: "list" },
      { k: "chaos_points", label: "Chaos points (one per line)", type: "list" },
    ],
  },
  {
    key: "lead_generation_and_sales",
    title: "Lead Generation & Sales",
    eyebrow: "Funnel + CRM",
    fields: [
      { k: "lead_sources", label: "Lead sources", type: "textarea" },
      { k: "lead_capture_process", label: "Lead capture", type: "textarea" },
      { k: "speed_to_lead", label: "Speed to lead" },
      { k: "follow_up_process", label: "Follow-up process", type: "textarea" },
      { k: "crm_or_pipeline", label: "CRM / pipeline", type: "textarea" },
      { k: "dropped_leads", label: "Dropped leads", type: "textarea" },
      { k: "sales_visibility_gaps", label: "Sales visibility gaps", type: "textarea" },
    ],
  },
  {
    key: "current_tools_and_systems",
    title: "Tools & Money Leaks",
    eyebrow: "Stack visibility",
    fields: [
      { k: "crm", label: "CRM" },
      { k: "calendar", label: "Calendar" },
      { k: "email_marketing", label: "Email Marketing" },
      { k: "project_management", label: "Project Management" },
      { k: "accounting", label: "Accounting" },
      { k: "communication", label: "Communication" },
      { k: "ai_tools", label: "AI Tools" },
      { k: "spreadsheets_or_workarounds", label: "Spreadsheets / Workarounds", type: "textarea" },
      { k: "tools_they_love", label: "Tools they love", type: "textarea" },
      { k: "tools_they_hate", label: "Tools they hate", type: "textarea" },
      { k: "wasted_tool_spend", label: "Wasted tool spend", type: "textarea" },
    ],
  },
  {
    key: "team_and_accountability",
    title: "Team & Accountability",
    eyebrow: "Org gaps",
    fields: [
      { k: "team_summary", label: "Team summary", type: "textarea" },
      { k: "role_clarity_gaps", label: "Role clarity gaps", type: "list" },
      { k: "owner_bottlenecks", label: "Owner bottlenecks", type: "list" },
      { k: "decision_rights_issues", label: "Decision rights issues", type: "list" },
      { k: "operating_cadence_gaps", label: "Operating cadence gaps", type: "list" },
    ],
  },
];

const NOT_PROVIDED = ["", "not provided", "unclear"];

function fieldKey(section, field) {
  return `${section}.${field}`;
}

function valueFromExtraction(extraction, sectionKey, fieldKey) {
  if (!extraction) return "";
  const section = extraction[sectionKey] || {};
  const v = section[fieldKey];
  if (Array.isArray(v)) return v.join("\n");
  return v == null ? "" : String(v);
}

function isMissing(v) {
  return NOT_PROVIDED.includes((v || "").trim().toLowerCase());
}

export default function ReviewProfileTab({ interviewId, data, setData }) {
  const extraction = data?.transcript_extraction;
  const existing = data?.reviewed_profile;

  // Seed the working state from the existing reviewed_profile if present,
  // otherwise from the raw transcript_extraction.
  const seed = useMemo(() => {
    const fields = {};
    const followups = {};
    for (const sec of SECTIONS) {
      for (const f of sec.fields) {
        const key = fieldKey(sec.key, f.k);
        const stored = existing?.fields?.[key];
        const extracted = valueFromExtraction(extraction, sec.key, f.k);
        fields[key] = stored != null ? stored : extracted;
        followups[key] = !!existing?.followups?.[key];
      }
    }
    return { fields, followups, notes: existing?.notes || "" };
  }, [extraction, existing]);

  const [working, setWorking] = useState(seed);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setWorking(seed); setDirty(false); }, [seed]);

  const setField = (key, value) => {
    setWorking((w) => ({ ...w, fields: { ...w.fields, [key]: value } }));
    setDirty(true);
  };
  const toggleFollowup = (key, on) => {
    setWorking((w) => ({ ...w, followups: { ...w.followups, [key]: !!on } }));
    setDirty(true);
  };
  const setNotes = (v) => { setWorking((w) => ({ ...w, notes: v })); setDirty(true); };

  const followupCount = Object.values(working.followups || {}).filter(Boolean).length;
  const totalFields = Object.keys(working.fields || {}).length;
  const missingCount = Object.values(working.fields || {}).filter(isMissing).length;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/interviews/${interviewId}/reviewed-profile`, {
        fields: working.fields,
        followups: working.followups,
        notes: working.notes,
      });
      setData?.((prev) => ({ ...(prev || {}), reviewed_profile: r.data.reviewed_profile }));
      setDirty(false);
      toast.success("Reviewed profile saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!extraction) {
    return (
      <div className="bg-white border border-navy/10 p-8 md:p-10" data-testid="review-profile-empty">
        <div className="eyebrow text-gold">Review Profile</div>
        <h3 className="font-display text-2xl text-navy mt-2">Nothing to review yet</h3>
        <div className="w-12 h-[2px] bg-gold mt-3" />
        <p className="text-sm text-navy/60 mt-4 max-w-xl">
          Paste a transcript on the <strong>Transcript</strong> tab and run the AI extraction first.
          Once we have a draft profile, this tab lets you correct facts inline and flag anything that still needs a follow-up before generating documents.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-navy/10 p-8 md:p-10" data-testid="review-profile-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="eyebrow text-gold">Step 02 · Review</div>
          <h3 className="font-display text-3xl text-navy mt-2">Review the Extracted Profile</h3>
          <div className="w-12 h-[2px] bg-gold mt-3" />
          <p className="text-sm text-navy/60 mt-4 max-w-2xl">
            Every field below is editable. Toggle <strong>Needs follow-up</strong> on anything still murky — those flags carry into the Data Request Checklist and Internal Prep Sheet documents.
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-4 text-xs">
            <Badge variant="outline" className="rounded-none border-navy/20 text-navy/70 uppercase tracking-wider font-semibold">
              {totalFields} fields
            </Badge>
            <Badge variant="outline" className="rounded-none border-gold text-gold uppercase tracking-wider font-semibold" data-testid="review-followup-count">
              {followupCount} follow-ups flagged
            </Badge>
            {missingCount > 0 && (
              <Badge variant="outline" className="rounded-none border-red-500/30 text-red-700 uppercase tracking-wider font-semibold" data-testid="review-missing-count">
                {missingCount} missing
              </Badge>
            )}
            {existing?.saved_at && (
              <span className="text-navy/50">Last saved {new Date(existing.saved_at).toLocaleString()}</span>
            )}
          </div>
        </div>
        <Button
          onClick={save}
          disabled={saving || !dirty}
          className="bg-gold hover:bg-gold/90 text-navy rounded-none h-11 px-6"
          data-testid="review-save-btn"
        >
          {saving ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
          ) : (
            <><Save className="w-4 h-4 mr-2" /> {dirty ? "Save Profile" : "Saved"}</>
          )}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        {SECTIONS.map((sec) => (
          <div key={sec.key} className="bg-ivory border border-navy/10 p-5" data-testid={`review-section-${sec.key}`}>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gold" />
              <span className="eyebrow text-gold">{sec.eyebrow}</span>
            </div>
            <h4 className="font-display text-xl text-navy mt-1">{sec.title}</h4>
            <div className="w-8 h-[2px] bg-gold mt-2 mb-4" />
            <div className="space-y-4">
              {sec.fields.map((f) => {
                const key = fieldKey(sec.key, f.k);
                const value = working.fields?.[key] ?? "";
                const flagged = !!working.followups?.[key];
                const missing = isMissing(value);
                const inputType = f.type || "text";
                return (
                  <div key={key} className={`space-y-1.5 ${flagged ? "border-l-2 border-gold pl-3 -ml-3" : ""}`}>
                    <div className="flex items-center justify-between gap-2">
                      <label className="text-[11px] uppercase tracking-wider text-navy/55 font-semibold flex items-center gap-1.5">
                        {f.label}
                        {missing && <AlertTriangle className="w-3 h-3 text-red-500" data-testid={`review-missing-${key}`} />}
                        {!missing && !flagged && <ShieldCheck className="w-3 h-3 text-emerald-600" />}
                      </label>
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-navy/55">
                        <span>Follow-up</span>
                        <Switch
                          checked={flagged}
                          onCheckedChange={(c) => toggleFollowup(key, c)}
                          data-testid={`review-followup-${key}`}
                          className="data-[state=checked]:bg-gold"
                        />
                      </div>
                    </div>
                    {inputType === "textarea" || inputType === "list" ? (
                      <Textarea
                        rows={inputType === "list" ? 4 : 2}
                        value={value}
                        onChange={(e) => setField(key, e.target.value)}
                        className={inputCls + " bg-white"}
                        data-testid={`review-field-${key}`}
                        placeholder={inputType === "list" ? "One per line…" : ""}
                      />
                    ) : (
                      <Input
                        value={value}
                        onChange={(e) => setField(key, e.target.value)}
                        className={inputCls + " bg-white"}
                        data-testid={`review-field-${key}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 bg-navy text-ivory p-6">
        <div className="eyebrow text-gold">Consultant Notes</div>
        <h4 className="font-display text-xl mt-1">Anything else to capture before doc gen?</h4>
        <div className="w-8 h-[2px] bg-gold mt-2 mb-4" />
        <Textarea
          rows={3}
          value={working.notes || ""}
          onChange={(e) => setNotes(e.target.value)}
          className="bg-navy/30 border-ivory/15 text-ivory rounded-none placeholder:text-ivory/40 focus-visible:ring-gold focus-visible:ring-1 focus-visible:ring-offset-0"
          placeholder="Quotes, context, vibes — anything the LLM should weight when generating the packet."
          data-testid="review-notes"
        />
      </div>

      <div className="mt-6 flex items-start gap-2 text-[11px] text-navy/40 italic">
        <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
        Saved reviews are preferred over the raw transcript extraction during document generation.
      </div>
    </div>
  );
}
